/**
 * Patient flow routes beyond the status step (NEW-WF-06). Kept apart from
 * `ScheduleApi` so screens and tests that do not use them stay unchanged.
 */
import {
  FlowNotificationListSchema,
  FlowReportSchema,
  FlowSignalResultSchema,
  FlowSubscriptionSchema,
  NextForMeSchema,
  type FlowNotificationList,
  type NextForMe,
  type FlowReport,
  type FlowSignalResult,
  type FlowSubscription,
  type FlowSubscriptionPut,
  type ReadyForDoctorRequestInput,
} from '@velora/contracts/flow';
import type { DoctorReason } from '@velora/contracts/lightboard';
import {
  AppointmentDetailSchema,
  ScheduleWriteResultSchema,
  type ScheduleWriteResult,
} from '@velora/contracts/schedule';
import { buildPath, type HttpClient } from '@velora/sdk';
import { intentKey } from './api.js';

export function readyForDoctor(
  http: HttpClient,
  appointmentId: string,
  reason: DoctorReason = 'hyg_exam',
  source: ReadyForDoctorRequestInput['source'] = 'desk',
  /** A line for the doctor, added to the clinical summary the server sends. */
  note?: string,
): Promise<FlowSignalResult> {
  return http.request(
    buildPath('/schedule/appointments/:id/ready-for-doctor', { id: appointmentId }),
    {
      method: 'POST',
      body: {
        idempotencyKey: intentKey(),
        reason,
        source,
        ...(note?.trim() ? { note: note.trim() } : {}),
      },
      schema: FlowSignalResultSchema,
    },
  );
}

/** Doctor in when the chart opens on the room's device (SF-09); quiet when not waiting. */
export function doctorIn(
  http: HttpClient,
  appointmentId: string,
  operatoryId: string,
): Promise<FlowSignalResult> {
  return http.request(buildPath('/schedule/appointments/:id/doctor-in', { id: appointmentId }), {
    method: 'POST',
    body: { idempotencyKey: intentKey(), operatoryId },
    schema: FlowSignalResultSchema,
  });
}

export function flowNotifications(
  http: HttpClient,
  since: string | null,
  signal?: AbortSignal,
): Promise<FlowNotificationList> {
  return http.request(
    buildPath('/schedule/flow/notifications', undefined, since ? { since } : {}),
    { schema: FlowNotificationListSchema, ...(signal ? { signal } : {}) },
  );
}

export function getFlowSubscription(http: HttpClient): Promise<FlowSubscription> {
  return http.request('/schedule/flow/subscription', { schema: FlowSubscriptionSchema });
}

export function putFlowSubscription(
  http: HttpClient,
  body: FlowSubscriptionPut,
): Promise<FlowSubscription> {
  return http.request('/schedule/flow/subscription', {
    method: 'PUT',
    body,
    schema: FlowSubscriptionSchema,
  });
}

export function patientFlowReport(http: HttpClient, from: string, to: string): Promise<FlowReport> {
  return http.request(buildPath('/analytics/reports/patient-flow', undefined, { from, to }), {
    schema: FlowReportSchema,
  });
}

/** Seat an appointment from outside the schedule (Light Board "Seated", a ping's Seat). */
export async function seatAppointment(
  http: HttpClient,
  appointmentId: string,
): Promise<ScheduleWriteResult> {
  const d = await http.request(buildPath('/schedule/appointments/:id', { id: appointmentId }), {
    schema: AppointmentDetailSchema,
  });
  return http.request(buildPath('/schedule/appointments/:id/status', { id: appointmentId }), {
    method: 'POST',
    body: {
      idempotencyKey: intentKey(),
      expectedVersion: d.appointment.version,
      flowStatus: 'chair',
      source: 'lightboard',
    },
    schema: ScheduleWriteResultSchema,
  });
}

/** "Next for me": exams ready, chairs running over, notes to sign (dentist role test). */
export function nextForMe(http: HttpClient, signal?: AbortSignal): Promise<NextForMe> {
  return http.request('/schedule/flow/next-for-me', {
    schema: NextForMeSchema,
    ...(signal ? { signal } : {}),
  });
}
